import { type ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Badge, Button, EmptyState, FormCheckbox, FormInput, FormSelect } from '@components/UI';
import { useMutation } from '@tanstack/react-query';
import { trpc, trpcUnbatchedClient } from '@/api/trpc';
import { RAW_RETURN_SUM_DESCRIPTION, RAW_RETURN_SUM_LABEL } from '@/components/Experiments/screenerOptimizer/returnMetricCopy';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { RouterOutputs, ScreenerGroupInput } from '../types/screenerEditor';

type OptimizerJob = RouterOutputs['screener']['optimizer']['job']['job'];
type OptimizerResultRow = RouterOutputs['screener']['optimizer']['listResultsForJob']['results'][number];
type EvaluateRuleOutput = RouterOutputs['screener']['optimizer']['evaluateRuleOnJob'];
type RecentJob = RouterOutputs['screener']['optimizer']['listRecentJobs']['jobs'][number];

function parseOptionalPositiveInt(raw: string): number | null {
    const trimmed = raw.trim();
    if (!trimmed.length) return null;
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed)) return null;
    return parsed > 0 ? parsed : null;
}

function toNumber(value: string | null | undefined): number {
    if (value == null) return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function clamp01Number(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.min(1, Math.max(0, value));
}

function formatPct01Number(value: number, decimals: number = 1): string {
    const pct = clamp01Number(value) * 100;
    return `${pct.toFixed(decimals)}%`;
}

function formatSignedPct(value: number, decimals: number = 2): string {
    if (!Number.isFinite(value)) return '-';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(decimals)}%`;
}

function profitColorClass(value: number): string {
    if (!Number.isFinite(value) || value === 0) return 'text-zinc-300';
    return value > 0 ? 'text-green-400' : 'text-red-400';
}

function badgeVariantForJobStatus(status: OptimizerJob['status']): ComponentProps<typeof Badge>['variant'] {
    switch (status) {
        case 'queued':
            return 'warning';
        case 'materializing':
        case 'optimizing':
        case 'validating':
            return 'info';
        case 'completed':
            return 'success';
        case 'failed':
            return 'danger';
        case 'cancelled':
            return 'secondary';
        default:
            return 'secondary';
    }
}

function formatTimestamp(ts: number): string {
    return dayjs.unix(ts).format('MMM D, YYYY h:mm A');
}

type BaselineKind = 'job' | 'result';

interface PerformanceSectionProps {
    rootGroupInput: ScreenerGroupInput | null;
    rootGroupInputError: string | null;
    initialJobId?: number | null;
    screenerRuleId?: number | null;
    onCapture?: (payload: {
        source: 'auto' | 'manual';
        jobId: number;
        rootGroupInput: ScreenerGroupInput;
        eval: EvaluateRuleOutput;
        label: string;
        note: string | null;
    }) => void;
}

const PerformanceSection = ({
    rootGroupInput,
    rootGroupInputError,
    initialJobId = null,
    screenerRuleId = null,
    onCapture
}: PerformanceSectionProps) => {
    const [jobIdInput, setJobIdInput] = useState(() => (initialJobId ? String(initialJobId) : ''));
    const [jobId, setJobId] = useState<number | null>(initialJobId);

    const [isLive, setIsLive] = useState(false);
    const [baselineKind, setBaselineKind] = useState<BaselineKind>('job');
    const [baselineResultId, setBaselineResultId] = useState<number | null>(null);

    const [lastEval, setLastEval] = useState<EvaluateRuleOutput | null>(null);
    const [evalError, setEvalError] = useState<string | null>(null);
    const [captureLabel, setCaptureLabel] = useState('');
    const [captureNote, setCaptureNote] = useState('');
    const [autoCaptureOnEvaluate, setAutoCaptureOnEvaluate] = useState(true);

    const [browseJobsOpen, setBrowseJobsOpen] = useState(false);
    const browseDropdownRef = useRef<HTMLDivElement>(null);

    // Click-outside handler for Browse Jobs dropdown
    useEffect(() => {
        if (!browseJobsOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (browseDropdownRef.current && !browseDropdownRef.current.contains(event.target as Node)) {
                setBrowseJobsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [browseJobsOpen]);

    // --- Queries ---

    const recentJobsQuery = useAppQuery({
        ...trpc.screener.optimizer.listRecentJobs.queryOptions({ limit: 50 }),
        staleTime: 60_000
    });

    const promotionsQuery = useAppQuery({
        ...trpc.screener.optimizer.promotionsByScreenerRuleId.queryOptions({
            screenerRuleId: screenerRuleId ?? 0
        }),
        enabled: screenerRuleId !== null && screenerRuleId > 0,
        staleTime: 60_000
    });

    const originJobIds = useMemo(() => {
        const promotions = promotionsQuery.data?.promotions ?? [];
        return new Set(promotions.map((p) => p.jobId));
    }, [promotionsQuery.data?.promotions]);

    // Auto-select originating job from promotions when no job loaded and no initialJobId
    const didAutoSelectRef = useRef(false);

    useEffect(() => {
        if (didAutoSelectRef.current) return;
        if (jobId !== null) return;
        if (initialJobId !== null) return;

        const promotions = promotionsQuery.data?.promotions ?? [];
        if (promotions.length === 0) return;

        const firstJobId = promotions[0].jobId;
        didAutoSelectRef.current = true;
        setJobIdInput(String(firstJobId));
        setJobId(firstJobId);
    }, [initialJobId, jobId, promotionsQuery.data?.promotions]);

    const jobQuery = useAppQuery(
        {
            ...trpc.screener.optimizer.job.queryOptions({ jobId: jobId ?? 0 }),
            enabled: jobId !== null
        },
        { fallbackErrorMessage: 'Failed to load optimizer job', showErrorToast: false }
    );

    const job = jobQuery.data?.job ?? null;

    const resultsQuery = useAppQuery(
        {
            ...trpc.screener.optimizer.listResultsForJob.queryOptions({
                jobId: jobId ?? 0,
                page: 1,
                pageSize: 200,
                sortBy: 'selectionScore',
                sortDirection: 'DESC'
            }),
            enabled: jobId !== null
        },
        { fallbackErrorMessage: 'Failed to load optimizer results', showErrorToast: false }
    );

    const results = resultsQuery.data?.results ?? [];

    useEffect(() => {
        if (baselineKind !== 'result') return;
        if (baselineResultId !== null) return;
        if (results.length === 0) return;
        setBaselineResultId(results[0].id);
    }, [baselineKind, baselineResultId, results]);

    const baselineResult: OptimizerResultRow | null = useMemo(() => {
        if (baselineKind !== 'result') return null;
        if (baselineResultId === null) return null;
        return results.find((r) => r.id === baselineResultId) ?? null;
    }, [baselineKind, baselineResultId, results]);

    // --- Evaluation ---

    const evalMutation = useMutation({
        mutationFn: (input: { jobId: number; rootGroup: ScreenerGroupInput }) =>
            trpcUnbatchedClient.screener.optimizer.evaluateRuleOnJob.mutate(input)
    });

    const evalRunIdRef = useRef(0);
    const rerunAfterPendingRef = useRef(false);

    const canEvaluate = jobId !== null && rootGroupInput !== null && rootGroupInputError === null;
    const canCapture = Boolean(onCapture) && jobId !== null && rootGroupInput !== null && lastEval !== null;

    const runEvaluate = useCallback(
        (reason: 'manual' | 'live') => {
            if (!canEvaluate) return;

            if (evalMutation.isPending && reason === 'live') {
                rerunAfterPendingRef.current = true;
                return;
            }

            const runId = ++evalRunIdRef.current;
            setEvalError(null);

            evalMutation.mutate(
                { jobId, rootGroup: rootGroupInput },
                {
                    onSuccess: (data) => {
                        if (evalRunIdRef.current !== runId) return;
                        setLastEval(data);

                        if (reason === 'manual' && onCapture && autoCaptureOnEvaluate) {
                            const label = captureLabel.trim();
                            const note = captureNote.trim().length ? captureNote.trim() : null;

                            onCapture({ source: 'auto', jobId, rootGroupInput, eval: data, label, note });
                            setCaptureLabel('');
                            setCaptureNote('');
                        }
                    },
                    onError: (error: Error) => {
                        if (evalRunIdRef.current !== runId) return;
                        setEvalError(error.message || 'Failed to evaluate rule');
                    }
                }
            );
        },
        [autoCaptureOnEvaluate, canEvaluate, captureLabel, captureNote, evalMutation, jobId, onCapture, rootGroupInput]
    );

    useEffect(() => {
        if (!isLive) return;
        if (!canEvaluate) return;
        if (evalMutation.isPending) return;
        if (!rerunAfterPendingRef.current) return;

        rerunAfterPendingRef.current = false;
        runEvaluate('live');
    }, [canEvaluate, evalMutation.isPending, isLive, runEvaluate]);

    useEffect(() => {
        if (!isLive) return;
        if (!canEvaluate) return;

        const timer = setTimeout(() => {
            runEvaluate('live');
        }, 400);

        return () => clearTimeout(timer);
    }, [canEvaluate, isLive, jobId, rootGroupInput, rootGroupInputError, runEvaluate]);

    useEffect(() => {
        setLastEval(null);
        setEvalError(null);
        rerunAfterPendingRef.current = false;
        evalRunIdRef.current += 1;
    }, [jobId]);

    // --- Handlers ---

    const handleLoadJob = () => {
        const parsed = parseOptionalPositiveInt(jobIdInput);

        if (!parsed) {
            setJobId(null);
            return;
        }

        setJobId(parsed);
    };

    const handleSelectRecentJob = (recentJob: RecentJob) => {
        setJobIdInput(String(recentJob.id));
        setJobId(recentJob.id);
        setBrowseJobsOpen(false);
    };

    // --- Comparison rows ---

    const comparisonRows = useMemo(() => {
        if (!lastEval) return null;

        const current = lastEval.metrics.inSample;

        if (baselineKind === 'job') {
            const baselineAvg = current.baselineAvgProfit;

            return [
                {
                    label: 'Avg Trade (raw)',
                    tooltip: 'Average raw profit/loss per trade that passed the screener rules',
                    current: formatSignedPct(current.avgProfit),
                    baseline: formatSignedPct(baselineAvg),
                    diff: formatSignedPct(current.avgProfit - baselineAvg)
                },
                {
                    label: RAW_RETURN_SUM_LABEL,
                    tooltip: RAW_RETURN_SUM_DESCRIPTION,
                    current: formatSignedPct(current.totalProfit),
                    baseline: formatSignedPct(current.totalCount * baselineAvg),
                    diff: formatSignedPct(current.totalProfit - current.totalCount * baselineAvg)
                },
                {
                    label: 'Win Rate',
                    tooltip: 'Percentage of passing trades that were profitable',
                    current: formatPct01Number(current.winRate, 1),
                    baseline: '—',
                    diff: '—'
                },
                {
                    label: 'Coverage',
                    tooltip: 'Fraction of total trades that pass the screener',
                    current: formatPct01Number(current.coverage, 1),
                    baseline: formatPct01Number(1, 1),
                    diff: formatSignedPct((current.coverage - 1) * 100, 1)
                },
                {
                    label: 'Trades',
                    tooltip: 'Number of trades that pass the screener vs total in the dataset',
                    current: String(current.passingCount),
                    baseline: String(current.totalCount),
                    diff: String(current.passingCount - current.totalCount)
                }
            ];
        }

        if (!baselineResult) return null;

        const baselineAvg = toNumber(baselineResult.avgProfit);
        const baselineCoverage = toNumber(baselineResult.coverage);
        const baselineWinRate = toNumber(baselineResult.winRate);

        return [
            {
                label: 'Avg Trade (raw)',
                tooltip: 'Average raw profit/loss per trade that passed the screener rules',
                current: formatSignedPct(current.avgProfit),
                baseline: formatSignedPct(baselineAvg),
                diff: formatSignedPct(current.avgProfit - baselineAvg)
            },
            {
                label: RAW_RETURN_SUM_LABEL,
                tooltip: RAW_RETURN_SUM_DESCRIPTION,
                current: formatSignedPct(current.totalProfit),
                baseline: formatSignedPct(baselineResult.totalProfit),
                diff: formatSignedPct(current.totalProfit - baselineResult.totalProfit)
            },
            {
                label: 'Win Rate',
                tooltip: 'Percentage of passing trades that were profitable',
                current: formatPct01Number(current.winRate, 1),
                baseline: formatPct01Number(baselineWinRate, 1),
                diff: formatSignedPct((current.winRate - baselineWinRate) * 100, 1)
            },
            {
                label: 'Coverage',
                tooltip: 'Fraction of total trades that pass the screener',
                current: formatPct01Number(current.coverage, 1),
                baseline: formatPct01Number(baselineCoverage, 1),
                diff: formatSignedPct((current.coverage - baselineCoverage) * 100, 1)
            },
            {
                label: 'Trades',
                tooltip: 'Number of trades that pass the screener vs total in the dataset',
                current: String(current.passingCount),
                baseline: String(baselineResult.tradeCount),
                diff: String(current.passingCount - baselineResult.tradeCount)
            }
        ];
    }, [baselineKind, baselineResult, lastEval]);

    // --- Empty state ---
    const showEmptyState = !jobId && !evalMutation.isPending && !lastEval;

    return (
        <div className="space-y-4">
            <div className="text-sm text-zinc-400">
                Evaluate the current screener rules against an existing optimizer job&apos;s evaluation dataset (fast baseline comparisons).
            </div>

            {/* Job selector row */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-3 min-w-0">
                    <FormInput
                        noMargin
                        label="Optimizer Job ID"
                        placeholder="e.g., 12345"
                        value={jobIdInput}
                        onChange={(e) => setJobIdInput(e.target.value)}
                    />
                </div>

                <div className="md:col-span-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={handleLoadJob}>
                        Load
                    </Button>
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                            setJobIdInput('');
                            setJobId(null);
                        }}
                    >
                        Clear
                    </Button>

                    <div className="relative" ref={browseDropdownRef}>
                        <Button size="sm" variant="secondary" onClick={() => setBrowseJobsOpen((prev) => !prev)}>
                            Browse Jobs
                        </Button>

                        {browseJobsOpen && (
                            <div className="absolute z-50 top-full left-0 mt-1 w-[420px] max-h-80 overflow-y-auto border border-zinc-700 rounded-lg bg-zinc-900 shadow-xl">
                                {recentJobsQuery.isLoading ? (
                                    <div className="p-3 text-xs text-zinc-400">Loading jobs...</div>
                                ) : !recentJobsQuery.data?.jobs.length ? (
                                    <EmptyState size="sm" title="No recent jobs found." />
                                ) : (
                                    recentJobsQuery.data.jobs.map((j) => (
                                        <button
                                            className="w-full text-left px-3 py-2 hover:bg-zinc-800 transition-colors border-b border-zinc-800 last:border-b-0"
                                            key={j.id}
                                            type="button"
                                            onClick={() => handleSelectRecentJob(j)}
                                        >
                                            <div className="flex items-center gap-2 text-xs">
                                                <span className="text-white font-medium">#{j.id}</span>
                                                <Badge variant={badgeVariantForJobStatus(j.status)}>{j.status}</Badge>
                                                <Badge variant="secondary">{j.entityType}</Badge>
                                                <Badge variant="secondary">{j.duration}</Badge>
                                                {originJobIds.has(j.id) && <Badge variant="info">origin</Badge>}
                                            </div>
                                            <div className="text-xs text-zinc-500 mt-1">
                                                {dayjs(j.createdAt).format('MMM D, YYYY h:mm A')}
                                                {j.totalEntries !== null && <span> · {j.totalEntries.toLocaleString()} entries</span>}
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="md:col-span-4 min-w-0">
                    <FormSelect
                        noMargin
                        helpText={
                            baselineKind === 'job'
                                ? 'Compares against average performance of all trades in the job'
                                : 'Compares against a specific optimizer result'
                        }
                        label="Baseline"
                        options={[
                            { value: 'job', label: 'Universe Average' },
                            { value: 'result', label: 'Specific Result' }
                        ]}
                        value={baselineKind}
                        onChange={(e) => setBaselineKind(e.target.value as BaselineKind)}
                    />
                </div>

                <div className="md:col-span-2">
                    <FormCheckbox checked={isLive} label="Live" onChange={(e) => setIsLive(e.target.checked)} />
                </div>
            </div>

            {baselineKind === 'result' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormSelect
                        label="Baseline Result"
                        options={results.map((r) => ({
                            value: String(r.id),
                            label: `resultId=${r.id} score=${r.selectionScore ?? '—'} trades=${r.tradeCount}`
                        }))}
                        value={baselineResultId !== null ? String(baselineResultId) : ''}
                        onChange={(e) => setBaselineResultId(parseOptionalPositiveInt(e.target.value))}
                    />

                    {baselineResult && (
                        <div className="text-xs text-zinc-400 flex flex-wrap gap-2 items-end">
                            <Badge variant="secondary">avg {formatSignedPct(toNumber(baselineResult.avgProfit), 2)}</Badge>
                            <Badge variant="secondary">lift {formatSignedPct(toNumber(baselineResult.lift), 2)}</Badge>
                            <Badge variant="secondary">cov {formatPct01Number(toNumber(baselineResult.coverage), 1)}</Badge>
                            <Badge variant="secondary">win {formatPct01Number(toNumber(baselineResult.winRate), 1)}</Badge>
                            {baselineResult.profitFactor !== null && (
                                <Badge variant="secondary">pf {baselineResult.profitFactor.toFixed(2)}</Badge>
                            )}
                        </div>
                    )}
                </div>
            )}

            {job && (
                <div className="text-xs text-zinc-400 flex flex-wrap gap-2">
                    <Badge variant={badgeVariantForJobStatus(job.status)}>
                        {job.status}
                        {jobId !== null ? ` (jobId=${jobId})` : ''}
                    </Badge>
                    <Badge variant="secondary">{job.entityType}</Badge>
                    <Badge variant="secondary">dur {job.duration}</Badge>
                    <Badge variant="secondary">h {job.horizonSeconds}s</Badge>
                    <Badge variant="secondary">{job.outcomeMetric}</Badge>
                    <Badge variant="secondary">fs {job.featureSetVersion}</Badge>
                </div>
            )}

            {rootGroupInputError && <div className="text-xs text-red-400">Invalid rules: {rootGroupInputError}</div>}

            {evalError && <div className="text-xs text-red-400">{evalError}</div>}

            {/* Empty state */}
            {showEmptyState && (
                <EmptyState
                    action={
                        <Button size="sm" variant="secondary" onClick={() => setBrowseJobsOpen(true)}>
                            Browse Jobs
                        </Button>
                    }
                    description={
                        <span className="block max-w-md">
                            Load an optimizer job to evaluate your screener rules against its dataset. Enter a job ID manually or browse
                            your recent jobs to get started.
                        </span>
                    }
                    title="No optimizer job loaded"
                />
            )}

            {/* Evaluate button row */}
            {!showEmptyState && (
                <div className="flex items-center gap-2">
                    <Button disabled={!canEvaluate || evalMutation.isPending} onClick={() => runEvaluate('manual')}>
                        {evalMutation.isPending ? 'Evaluating...' : 'Evaluate'}
                    </Button>

                    {lastEval && (
                        <div className="text-xs text-zinc-400">
                            {lastEval.ruleSummary ? (
                                <span title={lastEval.ruleSummary}>
                                    {lastEval.ruleSummary.length > 160 ? `${lastEval.ruleSummary.slice(0, 160)}...` : lastEval.ruleSummary}
                                </span>
                            ) : (
                                '—'
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Capture section */}
            {onCapture && lastEval && (
                <div className="border border-zinc-700 rounded bg-zinc-900/20 p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-zinc-400">Save this result so you can compare/revert later.</div>
                        <FormCheckbox
                            checked={autoCaptureOnEvaluate}
                            label="Auto-capture"
                            onChange={(e) => setAutoCaptureOnEvaluate(e.target.checked)}
                        />
                    </div>

                    {autoCaptureOnEvaluate ? (
                        <div className="text-xs text-zinc-500">
                            Experiments are captured automatically on each Evaluate.
                            {captureLabel.trim() && (
                                <span className="text-zinc-400 ml-1">
                                    Next label: <span className="text-zinc-300">{captureLabel.trim()}</span>
                                </span>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                            <div className="md:col-span-3">
                                <FormInput
                                    label="Label"
                                    placeholder="e.g. Tightened coverage"
                                    value={captureLabel}
                                    onChange={(e) => setCaptureLabel(e.target.value)}
                                />
                            </div>

                            <div className="md:col-span-2">
                                <FormInput
                                    label="Note (optional)"
                                    placeholder="What changed?"
                                    value={captureNote}
                                    onChange={(e) => setCaptureNote(e.target.value)}
                                />
                            </div>

                            <div className="md:col-span-1 flex">
                                <Button
                                    disabled={!canCapture}
                                    variant="primary"
                                    onClick={() => {
                                        if (!onCapture) return;
                                        if (jobId === null) return;
                                        if (!rootGroupInput) return;
                                        if (!lastEval) return;

                                        const label = captureLabel.trim();
                                        const note = captureNote.trim().length ? captureNote.trim() : null;

                                        onCapture({
                                            source: 'manual',
                                            jobId,
                                            rootGroupInput,
                                            eval: lastEval,
                                            label,
                                            note
                                        });

                                        setCaptureLabel('');
                                        setCaptureNote('');
                                    }}
                                >
                                    Capture
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Metrics */}
            {lastEval && (
                <div className="space-y-3">
                    {/* Primary metric cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="bg-zinc-900/40 border border-zinc-700 rounded p-3">
                            <div
                                className="text-xs text-zinc-400 cursor-help"
                                title="Average raw profit/loss per trade that passed the screener rules"
                            >
                                Avg Trade (raw)
                            </div>
                            <div className={`text-lg font-semibold ${profitColorClass(lastEval.metrics.inSample.avgProfit)}`}>
                                {formatSignedPct(lastEval.metrics.inSample.avgProfit, 2)}
                            </div>
                            <div
                                className="text-xs text-zinc-500 cursor-help"
                                title="Improvement over baseline average: (rule avg - baseline avg)"
                            >
                                lift {formatSignedPct(lastEval.metrics.inSample.lift, 2)}
                            </div>
                        </div>
                        <div className="bg-zinc-900/40 border border-zinc-700 rounded p-3">
                            <div className="text-xs text-zinc-400 cursor-help" title={RAW_RETURN_SUM_DESCRIPTION}>
                                {RAW_RETURN_SUM_LABEL}
                            </div>
                            <div className={`text-lg font-semibold ${profitColorClass(lastEval.metrics.inSample.totalProfit)}`}>
                                {formatSignedPct(lastEval.metrics.inSample.totalProfit, 2)}
                            </div>
                            <div className="text-xs text-zinc-500 cursor-help" title="Fraction of total trades that pass the screener">
                                trades {lastEval.metrics.inSample.passingCount} / {lastEval.metrics.inSample.totalCount} (
                                {formatPct01Number(lastEval.metrics.inSample.coverage, 1)})
                            </div>
                        </div>
                        <div className="bg-zinc-900/40 border border-zinc-700 rounded p-3">
                            <div className="text-xs text-zinc-400 cursor-help" title="Percentage of passing trades that were profitable">
                                Win Rate
                            </div>
                            <div className="text-lg font-semibold text-white">
                                {formatPct01Number(lastEval.metrics.inSample.winRate, 1)}
                            </div>
                            <div
                                className="text-xs text-zinc-500 cursor-help"
                                title="Ratio of gross profits to gross losses (> 1.0 is profitable)"
                            >
                                profit factor{' '}
                                {lastEval.metrics.inSample.profitFactor !== null ? lastEval.metrics.inSample.profitFactor.toFixed(2) : '—'}
                            </div>
                        </div>
                    </div>

                    {/* Comparison table */}
                    {comparisonRows && (
                        <div className="border border-zinc-700 rounded overflow-hidden">
                            <div className="bg-zinc-900/40 px-3 py-2 text-xs text-zinc-400">Current vs Baseline</div>
                            <table className="min-w-full text-xs">
                                <thead className="bg-zinc-900/20 text-zinc-400">
                                    <tr>
                                        <th className="text-left px-3 py-2">Metric</th>
                                        <th className="text-left px-3 py-2">Current</th>
                                        <th className="text-left px-3 py-2">Baseline</th>
                                        <th className="text-left px-3 py-2">Diff</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparisonRows.map((r) => {
                                        const diffN = Number(r.diff.replace('%', ''));
                                        const diffClass = Number.isFinite(diffN) ? profitColorClass(diffN) : 'text-zinc-300';

                                        return (
                                            <tr className="border-t border-zinc-800" key={r.label}>
                                                <td className="px-3 py-2 text-zinc-300 cursor-help" title={r.tooltip}>
                                                    {r.label}
                                                </td>
                                                <td className="px-3 py-2 text-white">{r.current}</td>
                                                <td className="px-3 py-2 text-zinc-300">{r.baseline}</td>
                                                <td className={`px-3 py-2 ${diffClass}`}>{r.diff}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Out-of-Sample Validation (holdout) */}
                    {lastEval.metrics.holdout && (
                        <div className="border border-zinc-700 rounded bg-zinc-900/20 p-3">
                            <div className="text-sm text-white font-medium mb-1">Out-of-Sample Validation</div>
                            <div className="text-xs text-zinc-500 mb-3">Performance on data the optimizer never saw during training</div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                    <div
                                        className="text-xs text-zinc-400 cursor-help"
                                        title="Improvement over baseline average on holdout data"
                                    >
                                        Lift
                                    </div>
                                    <div className={`text-sm font-semibold ${profitColorClass(lastEval.metrics.holdout.lift)}`}>
                                        {formatSignedPct(lastEval.metrics.holdout.lift, 2)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-zinc-400 cursor-help" title="Win rate on holdout (out-of-sample) trades">
                                        Win Rate
                                    </div>
                                    <div className="text-sm font-semibold text-white">
                                        {formatPct01Number(lastEval.metrics.holdout.winRate, 1)}
                                    </div>
                                </div>
                                <div>
                                    <div
                                        className="text-xs text-zinc-400 cursor-help"
                                        title="Fraction of holdout trades that pass the screener"
                                    >
                                        Coverage
                                    </div>
                                    <div className="text-sm font-semibold text-white">
                                        {formatPct01Number(lastEval.metrics.holdout.coverage, 1)}
                                    </div>
                                </div>
                                <div>
                                    <div
                                        className="text-xs text-zinc-400 cursor-help"
                                        title="Number of holdout trades passing vs total holdout trades"
                                    >
                                        Trades
                                    </div>
                                    <div className="text-sm font-semibold text-white">
                                        {lastEval.metrics.holdout.passingCount} / {lastEval.metrics.holdout.totalCount}
                                    </div>
                                </div>
                            </div>

                            <div className="text-xs text-zinc-500 mt-2">
                                Window: {formatTimestamp(lastEval.metrics.holdout.startTimestamp)} &rarr;{' '}
                                {formatTimestamp(lastEval.metrics.holdout.endTimestamp)}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PerformanceSection;
